import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import EmployeeListPage from './pages/EmployeeListPage';
import AttendanceListPage from './pages/AttendanceListPage';
import PayrollListPage from './pages/PayrollListPage';
import ReportPage from './pages/ReportPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import ManualPage from './pages/ManualPage';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import { UserProvider, UserContext } from './context/UserContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { FAQPage, TermsOfServicePage, PrivacyPolicyPage, MobileTopBar } from '@redpanda/ui';
import { Role } from '@redpanda/types';

const SCROLL_KEY_PREFIX = 'scroll:';

/**
 * 捲動管理（R16，照搬 CRM／PM）：重新整理時還原捲動位置、換頁才捲回頂端
 * 用「上一個路徑」判斷換頁，不用「是否首次」：開發模式 StrictMode 會重跑 effect
 */
const ScrollManager: React.FC = () => {
  const { pathname } = useLocation();
  const prevPathname = useRef<string | null>(null);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const save = () => {
      try {
        sessionStorage.setItem(SCROLL_KEY_PREFIX + window.location.pathname, String(window.scrollY));
      } catch { /* sessionStorage 不可用時略過 */ }
    };
    window.addEventListener('pagehide', save);
    return () => window.removeEventListener('pagehide', save);
  }, []);

  useLayoutEffect(() => {
    const prev = prevPathname.current;
    prevPathname.current = pathname;
    if (prev === pathname) return;
    if (prev === null) {
      try {
        const saved = sessionStorage.getItem(SCROLL_KEY_PREFIX + pathname);
        if (saved) window.scrollTo(0, Number(saved));
      } catch { /* sessionStorage 不可用時略過 */ }
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// 使用手冊依「上次停留的頁面」捲到對應章節（ManualPage navigationToSectionMap）
const SYSTEM_PATHS = ['/employees', '/attendance', '/payroll', '/reports'];

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useContext(UserContext)!;
  const location = useLocation();
  const { t } = useTranslation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = `RedPanda ${t('app.title')}`;
  }, [t]);

  useEffect(() => {
    const active = SYSTEM_PATHS.find(path => location.pathname.startsWith(path));
    if (active) sessionStorage.setItem('lastActivePage', active);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="app-loading bg-white dark:bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img
            src="/logo.png"
            alt="RedPanda Tech"
            className="w-48 h-48 mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden text-2xl font-semibold text-gray-900 dark:text-white mb-6">RedPanda HR</div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex">
      <Toaster position="bottom-right" />
      <ScrollManager />

      {isAuthenticated && (
        <Sidebar
          className="w-48 h-screen fixed left-0 top-0 z-50"
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div className={`flex-1 min-w-0 flex flex-col ${isAuthenticated ? 'md:ml-48' : ''}`}>
        {/* 手機版頂部列（R27）：取代原本浮動在左上角、會蓋住頁標題的選單按鈕 */}
        {isAuthenticated && (
          <MobileTopBar
            title={`RedPanda ${t('app.title')}`}
            onOpenMenu={() => setIsMobileSidebarOpen(true)}
            menuLabel={t('common.navigation.openMenu')}
          />
        )}

        <main className="flex-grow">
          <Routes>
            <Route path="/manual" element={<ManualPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />

            <Route element={<PrivateRoute />}>
              <Route path="/" element={<Navigate to="/employees" replace />} />
              <Route path="/employees" element={<EmployeeListPage />} />
              <Route path="/employees/archived" element={<EmployeeListPage isArchived />} />
              <Route path="/attendance" element={<AttendanceListPage />} />
              <Route path="/reports" element={<ReportPage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>

            {/* 薪資只給管理員 */}
            <Route element={<PrivateRoute allowedRoles={[Role.SUPER_ADMIN, Role.ADMIN]} />}>
              <Route path="/payroll" element={<PayrollListPage />} />
            </Route>

            <Route element={<PrivateRoute allowedRoles={[Role.SUPER_ADMIN]} />}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <ColorThemeProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </ColorThemeProvider>
  </Router>
);

export default App;
